import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Smartphone, Plus, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  getWaBusinessGroups,
  disconnectUnofficialWa,
  deleteUnofficialWaPermanent,
  assignInstanceToGroup,
  getInstanceWhatsappGroups,
  setOpsNotificationGroup,
  setInstancePurpose,
} from "../../../../api/channelIntegrations";
import { showToast } from "../../../../utils/showToast";
import UnofficialWaCard from "./UnofficialWaCard";
import QrCodeModal from "./QrCodeModal";
import BusinessGroupManager from "./BusinessGroupManager";
import DisconnectModal from "./DisconnectModal";
import RetireNumberModal from "./RetireNumberModal";
import useAuth from "../../../../hooks/useAuth";
import { roleRoutes } from "../../../../utils/roles";

export default function UnofficialWhatsAppManager({ integrations, onRefreshIntegrations }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [showQrModal, setShowQrModal] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [retireTargetId, setRetireTargetId] = useState(null);
  // Retirar o migrar un número es de supervisión: el backend exige supervisor
  // o administrador, así que al resto no se le ofrece.
  // Sin AuthProvider (tests, montajes aislados) no hay usuario: sin permisos.
  const user = useAuth()?.user;
  const canRetire = [
    roleRoutes.supervisor,
    roleRoutes.admin,
    roleRoutes.superadmin,
    roleRoutes.systemSuperadmin,
  ].includes(user?.role);
  const [deletingId, setDeletingId] = useState(null);
  // Grupos WhatsApp ("@g.us") por instancia, para el grupo de notificaciones.
  const [opsGroupsByInstance, setOpsGroupsByInstance] = useState({});
  const [loadingOpsGroupsId, setLoadingOpsGroupsId] = useState(null);
  const [savingOpsGroupId, setSavingOpsGroupId] = useState(null);
  const [savingPurposeId, setSavingPurposeId] = useState(null);

  const unofficialIntegrations = integrations.filter(
    (i) => i.channel === "whatsapp_unofficial" && i.status !== "inactive",
  );

  const fetchGroups = useCallback(async () => {
    const data = await getWaBusinessGroups();
    if (data?.ok) {
      setGroups(data.groups || []);
    } else {
      // `data.error.message` is the backend text or null (network, empty or
      // generic body); null falls back to the translated text.
      showToast(data?.error?.message ?? t("No se pudieron cargar los grupos de negocio"), false);
    }
    // `t` is left out on purpose: a new `t` must not refetch the groups (it only
    // translates the fallback of a failed load).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleDisconnect = async (integrationId) => {
    const result = await disconnectUnofficialWa(integrationId);
    if (result?.ok) {
      showToast(t("WhatsApp desconectado"), true);
      onRefreshIntegrations?.();
    } else {
      showToast(result?.error?.message ?? t("Error al desconectar"), false);
    }
  };

  // Eliminación permanente (sin modal) de un número desconectado: borra el
  // registro y limpia la instancia Evolution en el backend.
  const handleDeletePermanent = async (integrationId) => {
    setDeletingId(integrationId);
    const result = await deleteUnofficialWaPermanent(integrationId);
    if (result?.ok) {
      showToast(t("Número eliminado"), true);
      onRefreshIntegrations?.();
    } else {
      showToast(result?.error?.message ?? t("Error al eliminar el número"), false);
    }
    setDeletingId(null);
  };

  const handleAssignGroup = async (integrationId, groupId) => {
    const result = await assignInstanceToGroup(integrationId, groupId);
    if (result?.ok) {
      showToast(t("Grupo asignado"), true);
      onRefreshIntegrations?.();
    } else {
      showToast(result?.error?.message ?? t("Error al asignar grupo"), false);
    }
  };

  // Carga (bajo demanda) los grupos de WhatsApp del número conectado.
  const handleLoadOpsGroups = async (integrationId) => {
    setLoadingOpsGroupsId(integrationId);
    const data = await getInstanceWhatsappGroups(integrationId);
    if (data?.ok) {
      setOpsGroupsByInstance((prev) => ({
        ...prev,
        [integrationId]: data.groups || [],
      }));
    } else {
      showToast(
        data?.error?.message ??
          t("No se pudieron cargar los grupos. ¿La instancia está conectada?"),
        false,
      );
    }
    setLoadingOpsGroupsId(null);
  };

  const handleSetPurpose = async (integrationId, purpose) => {
    setSavingPurposeId(integrationId);
    const result = await setInstancePurpose(integrationId, purpose);
    if (result?.ok) {
      showToast(t("Uso del número actualizado"), true);
      onRefreshIntegrations?.();
    } else {
      showToast(result?.error?.message ?? t("Error al actualizar el uso del número"), false);
    }
    setSavingPurposeId(null);
  };

  const handleSetOpsGroup = async (integrationId, groupJid, groupName) => {
    setSavingOpsGroupId(integrationId);
    const result = await setOpsNotificationGroup(integrationId, groupJid, groupName);
    if (result?.ok) {
      showToast(
        groupJid ? t("Grupo de avisos configurado") : t("Grupo de avisos quitado"),
        true,
      );
      onRefreshIntegrations?.();
    } else {
      showToast(result?.error?.message ?? t("Error al configurar el grupo de avisos"), false);
    }
    setSavingOpsGroupId(null);
  };

  const connectedCount = unofficialIntegrations.filter(
    (i) => i.wa_connection_status === "open",
  ).length;
  const hasInstances = unofficialIntegrations.length > 0;

  return (
    <div className="space-y-3">
      {/* Sub-header */}
      <div>
        <h2 className="text-[12px] md:text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-2">
          {t("WhatsApp No Oficial")}
        </h2>

        <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-bg">
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-[#128C7E]/10 text-[#128C7E]">
            <Smartphone className="w-5 h-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-fg">{t("Conexión vía QR")}</h3>
              {connectedCount > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 h-5 md:h-4 rounded-full text-[12px] md:text-[10px] font-medium bg-status-success-soft text-status-success">
                  <CheckCircle2 className="w-2.5 h-2.5" aria-hidden="true" />
                  {connectedCount === 1
                    ? t("1 conectado")
                    : t("{{n}} conectados", { n: connectedCount })}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-fg-muted leading-relaxed">
              {t("Conecta números de WhatsApp escaneando un código QR. Útil para atender mensajes sin WhatsApp Business API.")}
            </p>

            {/* Disclaimer: la conexión vía QR queda fuera de la API oficial de
                WhatsApp. Un mal uso puede costar el número (baneo permanente). */}
            <div
              role="note"
              className="mt-3 flex items-start gap-2.5 rounded-lg border border-status-warning/30 bg-status-warning-soft/60 px-3 py-2.5"
            >
              <AlertTriangle
                className="w-4 h-4 shrink-0 mt-0.5 text-status-warning"
                aria-hidden="true"
              />
              <p className="text-[12px] md:text-[11px] leading-relaxed text-fg">
                <strong className="font-semibold text-fg">
                  {t("Conexión fuera de la API oficial:")}{" "}
                </strong>
                {t(
                  "este número se conecta vía QR, fuera de la API oficial de WhatsApp. Un mal uso de la aplicación (spam, envíos masivos o contenido que infrinja los términos de WhatsApp) puede provocar que tu número sea baneado y pierdas este número de forma definitiva. Úsalo de forma correcta.",
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Instancias conectadas */}
      {hasInstances && (
        <div className="space-y-1.5">
          {unofficialIntegrations.map((integration) => (
            <UnofficialWaCard
              key={integration.id}
              integration={integration}
              groups={groups}
              onDisconnect={setDisconnectTarget}
              onDeletePermanent={handleDeletePermanent}
              onRetire={canRetire ? (target) => setRetireTargetId(target.id) : undefined}
              onAssignGroup={handleAssignGroup}
              onSetPurpose={handleSetPurpose}
              deletingId={deletingId}
              opsGroups={opsGroupsByInstance[integration.id]}
              onLoadOpsGroups={handleLoadOpsGroups}
              onSetOpsGroup={handleSetOpsGroup}
              loadingOpsGroups={loadingOpsGroupsId === integration.id}
              savingOpsGroup={savingOpsGroupId === integration.id}
              savingPurpose={savingPurposeId === integration.id}
              showConnectionHistory={canRetire}
            />
          ))}
        </div>
      )}

      {/* Conectar nuevo */}
      <button
        type="button"
        onClick={() => setShowQrModal(true)}
        className={`w-full inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium transition-colors ${
          hasInstances
            ? "border border-dashed border-border text-fg-muted hover:border-brand-500 hover:text-brand-700 hover:bg-brand-50/40"
            : "hd-brand-solid bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
        }`}
      >
        <Plus className="w-4 h-4" aria-hidden="true" />
        {hasInstances ? t("Conectar otro número") : t("Conectar primer número")}
      </button>

      {/* Grupos de negocio */}
      <BusinessGroupManager groups={groups} onRefresh={fetchGroups} />

      {/* QR Modal */}
      <QrCodeModal
        isOpen={showQrModal}
        toggle={() => setShowQrModal(false)}
        groups={groups}
        onConnected={() => {
          onRefreshIntegrations?.();
          fetchGroups();
        }}
      />

      <RetireNumberModal
        isOpen={!!retireTargetId}
        integrationId={retireTargetId}
        onClose={() => setRetireTargetId(null)}
        onRetired={() => onRefreshIntegrations?.()}
      />

      {/* Disconnect confirmation */}
      <DisconnectModal
        isOpen={!!disconnectTarget}
        toggle={() => setDisconnectTarget(null)}
        integration={disconnectTarget}
        onConfirm={handleDisconnect}
      />
    </div>
  );
}
